import * as Core from '@skylab-shared/core';
import { Company, SaleKindModel } from '@skylab-shared/dataModel';
import { ContextKeys } from '../common/context-keys';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { CustomTransactionLocationError } from './violations/custom-transaction-location-error';


@Core.BO({ modelType: SaleKindModel })
export class SaleKindBO extends Core.BusinessObjectBase<SaleKindModel> {
    public constructor(
        weaver: Core.Weaver,
    ) {
        super(weaver);
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(ContextKeys.company),
        ]);

        this.registerCoreFeatures();
    }

    //#region Core feature actions

    public registerCoreFeatures(): void {
        this.readonly.setAsReadonly<SaleKindModel>([SaleKindModel, 'name']);
        readonlyTransactionLocation(this.readonly);
        this.setLocationAfterUseCustomTransactionChange();
        this.setLocationAfterCustomTransactionChange();
        this.setTransactionLocation();
        this.requireCustomTransactionLocation();
    }

    private setLocationAfterUseCustomTransactionChange(): void {
        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleKindModel, ['useCustomTransactionLocation'])(
            _ => this.setTransactionLocation()
        )));
    }

    private setLocationAfterCustomTransactionChange(): void {
        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleKindModel, ['customTransactionLocation'])(
            _ => this.setTransactionLocation()
        )));
    }

    private async setTransactionLocation(): Promise<void> {
        if (!this.model.useCustomTransactionLocation) {
            const company: Company = this.localContext.getPreloaded(ContextKeys.company)!;
            this.model.customTransactionLocation = company.city;
        }
    }

    private requireCustomTransactionLocation(): void {
        this.validation.addRule(
            [[SaleKindModel, 'useCustomTransactionLocation'], [SaleKindModel, 'customTransactionLocation']],
            [[SaleKindModel, 'customTransactionLocation']],
            ctx => {
                if (ctx.source.useCustomTransactionLocation) {
                    return ctx.source.customTransactionLocation.length > 0;
                }
                return true;
            },
            CustomTransactionLocationError
        );
    }

    //#endregion
}

export const readonlyTransactionLocation = (readonly: Core.ReadonlyEngine<SaleKindBO, SaleKindModel>) => readonly.addRule(
    [[SaleKindModel, 'useCustomTransactionLocation']],
    [[SaleKindModel, 'customTransactionLocation']],
    ctx => {
        return !ctx.source.useCustomTransactionLocation;
    }
);
